// Iteration 088 -- ConsentGate. Blocks the app when the current user has
// not consented to every active PolicyVersion. Sits inside RequireAuth at
// app root. Makes a single GET /me/consents on mount and re-renders the
// modal for each missing policy type in sequence.
import { useCallback, useEffect, useState } from "react";
import type { PolicyType, PolicyVersionPublic } from "@dugads/shared";
import { useAuth } from "@/lib/auth-context";
import * as api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Loader2 } from "lucide-react";

const policyLabels: Record<PolicyType, string> = {
  privacy: "Privacy Policy",
  terms: "Terms of Service",
  families: "Families Policy",
};

interface ConsentGateProps {
  children: React.ReactNode;
}

export function ConsentGate({ children }: ConsentGateProps) {
  const { user } = useAuth();
  const [missing, setMissing] = useState<PolicyType[]>([]);
  const [currentPolicy, setCurrentPolicy] = useState<PolicyVersionPublic | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadConsents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getMyConsents();
      setMissing(res.missing);
      if (res.missing.length > 0) {
        const p = await api.getCurrentPolicy(res.missing[0]);
        setCurrentPolicy(p.policy);
      } else {
        setCurrentPolicy(null);
      }
    } catch {
      // Endpoint requires auth -- if it fails, fall back to rendering children
      // (unauthenticated users shouldn't hit this anyway).
      setMissing([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Skip the fetch while the user is signed-out (e.g. during the brief
    // interval between logout() and RequireAuth redirecting to /login) --
    // firing it would produce a noisy 401 that the user sees in devtools.
    if (!user) {
      setMissing([]);
      setLoading(false);
      return;
    }
    void loadConsents();
  }, [user, loadConsents]);

  async function accept() {
    if (!currentPolicy) return;
    setSaving(true);
    setError(null);
    try {
      await api.recordConsent({
        // ConsentGate only ever shows self-consent policies (child_data is a
        // guardian-per-child consent, filtered out of `missing` server-side).
        policy_type: currentPolicy.policy_type as PolicyType,
        version: currentPolicy.version,
      });
      await loadConsents();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to record consent");
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <>{children}</>;

  // If nothing missing, render the app
  if (missing.length === 0 || !currentPolicy) return <>{children}</>;

  return (
    <>
      {children}
      <div className="fixed inset-0 z-[100] flex items-start justify-center bg-black/60 p-4 overflow-y-auto" data-dialog="consent">
        <Card className="mt-12 w-full max-w-2xl">
          <CardHeader>
            <CardTitle>
              {policyLabels[currentPolicy.policy_type as PolicyType]} -- please review
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              Version {currentPolicy.version} · Effective{" "}
              {new Date(currentPolicy.effective_at).toLocaleDateString()}
              {missing.length > 1 && ` · ${missing.length - 1} more to review`}
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <article className="max-h-80 overflow-y-auto rounded border bg-muted/20 p-3 text-sm whitespace-pre-wrap" data-region="body">
              {currentPolicy.body_markdown}
            </article>
            {error && <p className="text-sm text-destructive" data-error>{error}</p>}
            <div className="flex justify-end gap-2">
              <Button onClick={accept} disabled={saving} data-action="accept">
                {saving ? (
                  <>
                    <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                    Recording…
                  </>
                ) : (
                  "I agree"
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
